import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { EmailDraftResponse } from './useEmailDrafts';

type OutlookEmailActionInput = {
  draftId: string;
  attachmentFileId: number | null;
};

export function useOutlookEmailDraftActions(conversationId: string | undefined) {
  const queryClient = useQueryClient();

  const invalidateDrafts = () => {
    if (conversationId) {
      queryClient.invalidateQueries({
        queryKey: ['conversations', conversationId, 'email-drafts'],
      });
    }
  };

  const { mutateAsync: sendMutation, isPending: isSending } = useMutation<
    { data: EmailDraftResponse },
    Error,
    OutlookEmailActionInput
  >({
    mutationFn: ({ draftId, attachmentFileId }) =>
      api.post<EmailDraftResponse>(`email-drafts/${draftId}/outlook-send`, { attachmentFileId }),
    onSuccess: invalidateDrafts,
  });

  const { mutateAsync: saveMutation, isPending: isSaving } = useMutation<
    { data: EmailDraftResponse },
    Error,
    OutlookEmailActionInput
  >({
    mutationFn: ({ draftId, attachmentFileId }) =>
      api.post<EmailDraftResponse>(`email-drafts/${draftId}/outlook-save-draft`, {
        attachmentFileId,
      }),
    onSuccess: invalidateDrafts,
  });

  const sendViaOutlook = async (input: OutlookEmailActionInput): Promise<EmailDraftResponse> => {
    const response = await sendMutation(input);
    return response.data;
  };

  const saveToOutlookDrafts = async (
    input: OutlookEmailActionInput,
  ): Promise<EmailDraftResponse> => {
    const response = await saveMutation(input);
    return response.data;
  };

  return { sendViaOutlook, saveToOutlookDrafts, isSending, isSaving };
}
